import { ClaimSource, ClientType } from "Types";
import { limitChars, nullableString } from "Utils";
import * as yup from "Utils/yup";
import { GroupMappingFormData, OIDCFormData, SAMLFormData } from "../types";

const organizationRoleMappings = () =>
  yup
    .array()
    .required()
    .min(1)
    .of(
      yup.object().shape(
        {
          mapping: limitChars().when(["org_role"], {
            is: (org_role: string) => org_role,
            then: limitChars().required("Group name is required"),
          }),
          org_role: limitChars().when(["mapping"], {
            is: (mapping: string) => mapping,
            then: limitChars().required("Please choose a role"),
          }),
        },
        [["org_role", "mapping"]],
      ),
    )
    .uniqueProperty("mapping", "Group names must be unique");

export const OIDCFormSchema: yup.SchemaOf<OIDCFormData> = yup
  .object()
  .shape({
    // client config component
    client_id: limitChars(500).required("This field is required"),

    client_type: yup.mixed<ClientType>().required("This field is required"),

    client_secret: yup
      .string()
      .when(["client_type"], {
        is: (clientType: ClientType) => clientType === "confidential",
        then: limitChars(500).required("This field is required"),
      })
      .required("This field is required"),

    claim_source: yup.mixed<ClaimSource>().required("This field is required"),

    // provider config component
    authorization_endpoint: limitChars(500).required("This field is required"),
    token_endpoint: limitChars(500).required("This field is required"),

    user_info_endpoint: yup
      .mixed()
      .nullable()
      .when("claim_source", {
        is: (claimSource: ClaimSource) => claimSource === "user_info_endpoint",
        then: yup.string().required("This field is required"),
      }),

    jwks_endpoint: nullableString,
    token_audience: nullableString,
    token_algorithm: nullableString,
    issuer: nullableString,

    scopes: limitChars(500).required("This field is required"),

    // user mapping component
    user_email_mapping: limitChars(500).required("This field is required"),
    user_first_name_mapping: nullableString,
    user_last_name_mapping: nullableString,
    user_groups_mapping: limitChars(500).required("This field is required"),

    org_role_mappings: organizationRoleMappings(),
  })
  .required();

export const SAMLFormSchema: yup.SchemaOf<SAMLFormData> = yup
  .object()
  .shape({
    // provider config component
    metadata_url: limitChars(500).required("This field is required"),

    // user mapping component
    user_email_mapping: limitChars(500).required("This field is required"),
    user_first_name_mapping: nullableString,
    user_last_name_mapping: nullableString,
    user_groups_mapping: limitChars(500).required("This field is required"),

    org_role_mappings: organizationRoleMappings(),
  })
  .required();

// group mapping row validation works like this:
// 1. a row can be completely empty - mainly for better UX - empty rows are removed from the data on form submit
// 2. once a row has a group mapping name or a selected group, both fields are required and validation messages will show up beneath the empty one on form submit
// 3. group mapping name can have no spaces
export const GroupMappingFormSchema: yup.SchemaOf<GroupMappingFormData> =
  yup.object({
    group_mappings: yup.array().of(
      yup
        .object()
        .shape(
          {
            mapping: limitChars().when(["group_id"], {
              is: (group_id: string) => group_id,
              then: limitChars().required("Group name is required"),
            }),
            group_id: limitChars().when(["mapping"], {
              is: (mapping: string) => mapping,
              then: limitChars().required("Please choose a group"),
            }),
          },
          [["group_id", "mapping"]],
        )
        .optional(),
    ),
  });
